export enum CatalogTypeCategory {
  PART_NUMBER = "PART_NUMBER",
  FEE = "FEE",
  NOTE = "NOTE",
  DELIVERY_METHOD = "DELIVERY_METHOD",
  DELIVERY_SPEED = "DELIVERY_SPEED",
  RFQ_STATUS = "RFQ_STATUS",
}

export interface CatalogType {
  id: number;
  category: CatalogTypeCategory;
  value: string;
  label: string;
  displayOrder: number;
  /** Tax rate % for TAX fee type; unused for other types. */
  ratePercent?: number | null;
  /** Flat $ amount (RCS_ADMIN_FEE per-quote charge); unused for other types. */
  flatAmount?: number | null;
  /** RFQ status chip colors (#RRGGBB). */
  colorBg?: string | null;
  colorFg?: string | null;
  colorBorder?: string | null;
}

/** Friendly labels for known part-number type codes. */
export const PART_NUMBER_TYPE_LABELS: Record<string, string> = {
  ACTUAL: "Actual Part #",
  ACTUAL_MFG: "Actual MFG",
};

/** Map typed/friendly part-number labels to stable storage codes. */
export function normalizePartNumberTypeValue(raw: string): string {
  const trimmed = raw.trim().replace(/\s+/g, " ");
  if (!trimmed) {
    return "";
  }
  const compact = trimmed.toUpperCase().replace(/[\s_#-]+/g, "");
  if (
    compact === "ACTUALPART" ||
    compact === "ACTUALPARTNUMBER" ||
    compact === "ACTUALPARTNUM"
  ) {
    return "ACTUAL";
  }
  if (compact === "ACTUALMFG" || compact === "ACTUALMANUFACTURER") {
    return "ACTUAL_MFG";
  }
  if (trimmed.toUpperCase() === "ACTUAL") {
    return "ACTUAL";
  }
  const asCode = trimmed.toUpperCase().replace(/\s+/g, "_");
  if (asCode === "ACTUAL_MFG") {
    return "ACTUAL_MFG";
  }
  return trimmed.toUpperCase();
}

/** Match server-side catalog value normalization. */
export function normalizeCatalogTypeValue(
  category: CatalogTypeCategory,
  raw: string
): string {
  const trimmed = raw.trim().replace(/\s+/g, " ");
  if (!trimmed) {
    return "";
  }
  if (category === CatalogTypeCategory.NOTE) {
    return trimmed.replace(/\s+/g, "_").toLowerCase();
  }
  if (category === CatalogTypeCategory.FEE) {
    return trimmed.replace(/\s+/g, "_").toUpperCase();
  }
  if (category === CatalogTypeCategory.PART_NUMBER) {
    return normalizePartNumberTypeValue(trimmed);
  }
  if (
    category === CatalogTypeCategory.DELIVERY_METHOD ||
    category === CatalogTypeCategory.DELIVERY_SPEED
  ) {
    // Preserve QB / display strings exactly (e.g. "2nd Day Air", "n/a").
    return trimmed;
  }
  if (category === CatalogTypeCategory.RFQ_STATUS) {
    return trimmed.replace(/\s+/g, "_").toUpperCase();
  }
  return trimmed.toUpperCase();
}

export function defaultCatalogTypeLabel(
  category: CatalogTypeCategory,
  value: string,
  rawLabel?: string
): string {
  const provided = (rawLabel ?? "").trim();
  if (provided) {
    return provided;
  }
  if (category === CatalogTypeCategory.PART_NUMBER) {
    return PART_NUMBER_TYPE_LABELS[value] ?? value;
  }
  if (
    category === CatalogTypeCategory.FEE ||
    category === CatalogTypeCategory.NOTE ||
    category === CatalogTypeCategory.RFQ_STATUS
  ) {
    return value
      .split("_")
      .filter(Boolean)
      .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
      .join(" ");
  }
  if (
    category === CatalogTypeCategory.DELIVERY_METHOD ||
    category === CatalogTypeCategory.DELIVERY_SPEED
  ) {
    return value;
  }
  return value;
}

/** Display label for a part-number type code (catalog label, then known friendly names). */
export function partNumberTypeLabel(
  type: string,
  partNumberTypes?: CatalogType[]
): string {
  if (!type) {
    return "";
  }
  const fromCatalog = partNumberTypes
    ?.find((t) => t.value === type)
    ?.label?.trim();
  if (fromCatalog && fromCatalog !== type) {
    return fromCatalog;
  }
  return PART_NUMBER_TYPE_LABELS[type] ?? fromCatalog ?? type;
}
